import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const ago = (iso) => {
  if (!iso) return "";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : new Date(iso).toLocaleDateString();
};

export const CuratorBar = ({ onFinished }) => {
  const [status, setStatus] = useState(null);
  const timer = useRef(null);

  const poll = () => adminApi("/admin/curator/status").then((s) => {
    setStatus(s);
    clearTimeout(timer.current);
    if (s.last_run?.status === "running") timer.current = setTimeout(poll, 5000);
    return s;
  }).catch(() => {});

  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prevRunning = useRef(false);
  const running = status?.last_run?.status === "running";
  useEffect(() => {
    if (prevRunning.current && !running) {
      const r = status.last_run;
      if (r.status === "failed") toast.error("Curator run failed. Please try again.");
      else toast.success(r.added || r.pulse_added ? `${r.added || 0} signal(s) and ${r.pulse_added || 0} pulse brief(s) ready for review` : "No new stories scored high enough this time");
      onFinished();
    }
    prevRunning.current = running;
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    try { await adminApi("/admin/curator/run", { method: "POST" }); toast.success("Curator started — scanning your trusted sources"); poll(); }
    catch (e) { toast.error(e.message); }
  };

  const last = status?.last_run;
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--accent-violet)]/25 rounded-xl p-5 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="curator-bar">
      <div>
        <p className="text-white font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[var(--accent-violet-light)]" /> AI Curator</p>
        <p className="text-xs text-[var(--text-secondary)] mt-1" data-testid="curator-status">
          {status ? `${status.schedule} · up to ${status.max_signals} signals scoring ${status.min_score}+/10` : "Loading..."}
          {last && !running && ` · Last run ${ago(last.finished_at || last.started_at)}: ${last.status === "failed" ? "failed" : `${last.added || 0} signals + ${last.pulse_added || 0} pulse briefs from ${last.candidates || 0} new stories`}`}
          {running && " · Scanning sources and scoring stories (1–3 min)..."}
        </p>
      </div>
      <button onClick={run} disabled={running} className="flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60" data-testid="curator-run-btn">
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {running ? "Curating..." : "Find new signals"}
      </button>
    </div>
  );
};
